import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { Globe, Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { Input } from '../components/ui/Input';
import useLocale from '../contexts/LocaleContext';
import { authApi, handleApiError } from '../lib/api-client';
import logoRw from '../logo-rw.png';

const forgotPasswordSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
});

const resetPasswordSchema = z
  .object({
    password: z.string().min(6, 'Password must be at least 6 characters'),
    confirmPassword: z.string().min(6, 'Password must be at least 6 characters'),
  })
  .refine(data => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

type ForgotPasswordForm = z.infer<typeof forgotPasswordSchema>;
type ResetPasswordForm = z.infer<typeof resetPasswordSchema>;

export default function ForgotPassword() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const step = (searchParams.get('step') as 'email' | 'otp' | 'password' | 'success') || 'email';
  const { locale, setLocale, dictionary } = useLocale();
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);

  const {
    register: registerEmail,
    handleSubmit: handleSubmitEmail,
    formState: { errors: emailErrors },
  } = useForm<ForgotPasswordForm>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const {
    register: registerPassword,
    handleSubmit: handleSubmitPassword,
    formState: { errors: passwordErrors },
  } = useForm<ResetPasswordForm>({
    resolver: zodResolver(resetPasswordSchema),
  });

  const requestResetMutation = useMutation({
    mutationFn: (email: string) => authApi.requestPasswordReset(email),
    onSuccess: (_, email) => {
      setSubmittedEmail(email);
      setSearchParams({ step: 'otp' });
      toast.success(dictionary['auth.otp_sent'] || 'Code sent to your email');
    },
    onError: error => {
      handleApiError(error);
    },
  });

  const verifyOtpMutation = useMutation({
    mutationFn: (otpCode: string) => authApi.verifyOtp(submittedEmail, otpCode),
    onSuccess: () => {
      setSearchParams({ step: 'password' });
      toast.success(dictionary['auth.otp_verified'] || 'Code verified successfully');
    },
    onError: error => {
      handleApiError(error);
      setOtp(['', '', '', '', '', '']);
      otpRefs.current[0]?.focus();
    },
  });

  const resetPasswordMutation = useMutation({
    mutationFn: (password: string) => authApi.resetPasswordWithOtp(submittedEmail, otp.join(''), password),
    onSuccess: () => {
      toast.success(dictionary['auth.password_reset_success'] || 'Password reset successful! Redirecting to login...');
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    },
    onError: error => {
      handleApiError(error);
    },
  });

  const onSubmitEmail = async (data: ForgotPasswordForm) => {
    await requestResetMutation.mutateAsync(data.email);
  };

  const handleOtpChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return; // Only allow digits

    const newOtp = [...otp];
    newOtp[index] = value.slice(-1); // Only take last character
    setOtp(newOtp);

    // Auto-jump to next input
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 6 digits are entered
    if (index === 5 && value) {
      const fullOtp = [...newOtp.slice(0, 5), value].join('');
      if (fullOtp.length === 6) {
        verifyOtpMutation.mutate(fullOtp);
      }
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    if (!/^\d+$/.test(pastedData)) return;

    const newOtp = pastedData.split('').concat(['', '', '', '', '', '']).slice(0, 6);
    setOtp(newOtp);

    if (pastedData.length === 6) {
      otpRefs.current[5]?.focus();
      verifyOtpMutation.mutate(pastedData);
    } else {
      otpRefs.current[pastedData.length]?.focus();
    }
  };

  const onSubmitPassword = async (data: ResetPasswordForm) => {
    await resetPasswordMutation.mutateAsync(data.password);
  };

  // Auto-focus first OTP input when step changes to 'otp'
  useEffect(() => {
    if (step === 'otp') {
      setTimeout(() => otpRefs.current[0]?.focus(), 100);
    }
  }, [step]);

  return (
    <div className='min-h-screen flex items-center justify-center bg-slate-50 py-12 px-4'>
      <div className='max-w-sm w-full relative'>
        <div className='bg-white rounded-lg p-8 relative'>
          {/* Language Switcher */}
          <div className='absolute top-4 right-4'>
            <button
              onClick={() => setLanguageMenuOpen(!languageMenuOpen)}
              className='p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors'
              title='Change language'
            >
              <Globe className='h-5 w-5' />
            </button>

            {/* Language Dropdown */}
            {languageMenuOpen && (
              <>
                <div className='fixed inset-0 z-10' onClick={() => setLanguageMenuOpen(false)} />
                <div className='absolute right-0 mt-2 w-40 bg-white border border-gray-200 rounded-md shadow-lg z-20'>
                  <div className='py-1'>
                    <button
                      onClick={() => {
                        setLocale('en');
                        setLanguageMenuOpen(false);
                      }}
                      className={`w-full flex items-center px-3 py-2 text-sm transition-colors ${
                        locale === 'en' ? 'bg-secondary text-primary' : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      🇺🇸 English
                    </button>
                    <button
                      onClick={() => {
                        setLocale('rw');
                        setLanguageMenuOpen(false);
                      }}
                      className={`w-full flex items-center px-3 py-2 text-sm transition-colors ${
                        locale === 'rw' ? 'bg-secondary text-primary' : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      🇷🇼 Kinyarwanda
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className='text-center mb-6'>
            <div className='flex justify-center mb-4'>
              <img src={logoRw} alt='Republic of Rwanda Logo' className='h-16 w-16' />
            </div>
          </div>

          {step === 'email' && (
            <>
              <div className='text-center mb-6'>
                <h1 className='text-2xl font-bold text-gray-900 mb-2'>
                  {dictionary['auth.forgot_password_title'] || 'Forgot Password?'}
                </h1>
                <p className='text-gray-600'>
                  {dictionary['auth.forgot_password_description'] ||
                    "No worries! Enter your email address and we'll send you a code to reset your password."}
                </p>
              </div>

              <form onSubmit={handleSubmitEmail(onSubmitEmail)} className='space-y-4'>
                <div>
                  <label htmlFor='email' className='block text-sm font-medium text-gray-700 mb-2'>
                    {dictionary['auth.email'] || 'Email Address'}
                  </label>
                  <Input
                    id='email'
                    type='email'
                    placeholder={dictionary['auth.email_placeholder'] || 'Enter your email address'}
                    autoComplete='email'
                    error={emailErrors.email?.message}
                    {...registerEmail('email')}
                  />
                </div>

                <div className='flex flex-col gap-2 pt-2'>
                  <button
                    type='submit'
                    disabled={requestResetMutation.isPending}
                    className='w-full bg-primary text-white py-2 px-4 rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors'
                  >
                    {requestResetMutation.isPending ? (
                      <>
                        <Loader2 className='h-4 w-4 animate-spin' />
                        {dictionary['common.loading'] || 'Sending...'}
                      </>
                    ) : (
                      dictionary['auth.send_code'] || 'Send Code'
                    )}
                  </button>

                  <Link
                    to='/login'
                    className='w-full text-center bg-gray-100 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-200 transition-colors'
                  >
                    {dictionary['common.cancel'] || 'Cancel'}
                  </Link>
                </div>
              </form>
            </>
          )}

          {step === 'otp' && (
            <>
              <div className='text-center mb-6'>
                <h1 className='text-2xl font-bold text-gray-900 mb-2'>
                  {dictionary['auth.enter_otp_title'] || 'Enter Code'}
                </h1>
                <p className='text-gray-600'>
                  {dictionary['auth.enter_otp_description'] || 'Enter the 6-digit code sent to your email'}
                </p>
              </div>

              <div className='space-y-4'>
                <div className='flex justify-center gap-2'>
                  {otp.map((digit, index) => (
                    <input
                      key={index}
                      ref={el => (otpRefs.current[index] = el)}
                      type='text'
                      inputMode='numeric'
                      maxLength={1}
                      value={digit}
                      onChange={e => handleOtpChange(index, e.target.value)}
                      onKeyDown={e => handleOtpKeyDown(index, e)}
                      onPaste={index === 0 ? handleOtpPaste : undefined}
                      className='w-12 h-12 text-center text-lg font-semibold border-2 border-slate-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/10 focus:border-primary'
                      disabled={verifyOtpMutation.isPending}
                    />
                  ))}
                </div>

                {verifyOtpMutation.isPending && (
                  <div className='flex items-center justify-center gap-2 text-sm text-gray-600'>
                    <Loader2 className='h-4 w-4 animate-spin' />
                    {dictionary['auth.verifying_otp'] || 'Verifying...'}
                  </div>
                )}

                <div className='bg-amber-50 border border-amber-200 rounded-md p-3'>
                  <p className='text-sm text-amber-800'>
                    {dictionary['auth.forgot_password_tip'] ||
                      "Can't find the email? Check your spam or junk folder. The email should arrive within a few minutes."}
                  </p>
                </div>

                <button
                  type='button'
                  onClick={() => {
                    setSearchParams({ step: 'email' });
                    setOtp(['', '', '', '', '', '']);
                  }}
                  className='w-full text-sm text-primary hover:text-primary/80 font-medium'
                >
                  {dictionary['auth.use_different_email'] || 'Use different email'}
                </button>
              </div>
            </>
          )}

          {step === 'password' && (
            <>
              <div className='text-center mb-6'>
                <h1 className='text-2xl font-bold text-gray-900 mb-2'>
                  {dictionary['auth.reset_password_title'] || 'Reset Password'}
                </h1>
                <p className='text-gray-600'>
                  {dictionary['auth.reset_password_description'] || 'Enter your new password below.'}
                </p>
              </div>

              <form onSubmit={handleSubmitPassword(onSubmitPassword)} className='space-y-4'>
                <div>
                  <label htmlFor='password' className='block text-sm font-medium text-gray-700 mb-2'>
                    {dictionary['auth.new_password'] || 'New Password'}
                  </label>
                  <Input
                    id='password'
                    type='password'
                    placeholder={dictionary['auth.new_password'] || 'Enter new password'}
                    autoComplete='new-password'
                    error={passwordErrors.password?.message}
                    {...registerPassword('password')}
                  />
                </div>

                <div>
                  <label htmlFor='confirmPassword' className='block text-sm font-medium text-gray-700 mb-2'>
                    {dictionary['auth.confirm_new_password'] || 'Confirm New Password'}
                  </label>
                  <Input
                    id='confirmPassword'
                    type='password'
                    placeholder={dictionary['auth.confirm_new_password'] || 'Confirm new password'}
                    autoComplete='new-password'
                    error={passwordErrors.confirmPassword?.message}
                    {...registerPassword('confirmPassword')}
                  />
                </div>

                <button
                  type='submit'
                  disabled={resetPasswordMutation.isPending}
                  className='w-full bg-primary text-white py-2 px-4 rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors'
                >
                  {resetPasswordMutation.isPending ? (
                    <>
                      <Loader2 className='h-4 w-4 animate-spin' />
                      {dictionary['common.loading'] || 'Resetting...'}
                    </>
                  ) : (
                    dictionary['auth.reset_password_button'] || 'Reset Password'
                  )}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
